import React, { useEffect, useState } from "react";

import {
    RefreshCw,
    Warehouse,
    MapPin,
    Thermometer,
    Package,
    CheckCircle2,
    AlertTriangle,
    Snowflake,
    X,
    Eye,
    Boxes
} from "lucide-react";

import {
    getChambers,
    getInventoryByChamber
} from "../services/api";

import "./Chambers.css";


const Chambers = () => {

    // =====================================================
    // STATE
    // =====================================================

    const [chambers, setChambers] = useState([]);

    const [loading, setLoading] = useState(true);

    const [refreshing, setRefreshing] = useState(false);

    const [error, setError] = useState("");

    const [selectedChamber, setSelectedChamber] =
        useState(null);

    const [chamberInventory, setChamberInventory] =
        useState([]);

    const [inventoryLoading, setInventoryLoading] =
        useState(false);

    const [inventoryError, setInventoryError] =
        useState("");


    // =====================================================
    // LOAD CHAMBERS
    // =====================================================

    const loadChambers = async (showRefresh = false) => {

        try {

            if (showRefresh) {
                setRefreshing(true);
            } else {
                setLoading(true);
            }

            setError("");

            const response = await getChambers();

            const data =
                response?.data?.data ||
                response?.data ||
                [];

            setChambers(
                Array.isArray(data)
                    ? data
                    : []
            );

        } catch (err) {

            console.error(
                "Chambers Error:",
                err
            );

            setError(
                err?.response?.data?.message ||
                "Unable to load chamber information."
            );

        } finally {

            setLoading(false);
            setRefreshing(false);

        }

    };


    // =====================================================
    // INITIAL LOAD
    // =====================================================

    useEffect(() => {

        loadChambers();

    }, []);


    // =====================================================
    // REFRESH
    // =====================================================

    const handleRefresh = () => {

        loadChambers(true);

    };


    // =====================================================
    // FORMAT NUMBER
    // =====================================================

    const formatNumber = (value) => {

        return Number(
            value || 0
        ).toLocaleString(
            "en-IN",
            {
                maximumFractionDigits: 2
            }
        );

    };


    // =====================================================
    // CHAMBER DISPLAY NAME
    //
    // DATABASE:
    // C-01 -> A
    // C-02 -> B
    // C-03 -> C
    // C-04 -> D
    //
    // Database values are NOT changed.
    // Only frontend display is changed.
    // =====================================================

    const getChamberDisplayName = (chamberNo) => {

        const chamberMap = {

            "C-01": "A",

            "C-02": "B",

            "C-03": "C",

            "C-04": "D"

        };

        const normalized =
            String(
                chamberNo || ""
            )
                .trim()
                .toUpperCase();

        return (
            chamberMap[normalized] ||
            chamberNo ||
            "-"
        );

    };


    // =====================================================
    // GET CAPACITY
    // =====================================================

    const getCapacity = (chamber) => {

        return Number(
            chamber?.capacity_tons ??
            chamber?.capacityTons ??
            0
        );

    };


    // =====================================================
    // GET OCCUPIED
    // =====================================================

    const getOccupied = (chamber) => {

        return Number(
            chamber?.occupied_tons ??
            chamber?.occupiedTons ??
            0
        );

    };


    // =====================================================
    // GET AVAILABLE
    // =====================================================

    const getAvailable = (chamber) => {

        const value =
            chamber?.available_tons ??
            chamber?.availableTons;

        if (
            value !== undefined &&
            value !== null
        ) {

            return Number(value);

        }

        return Math.max(
            0,
            getCapacity(chamber) -
            getOccupied(chamber)
        );

    };


    // =====================================================
    // GET OCCUPANCY
    // =====================================================

    const getOccupancy = (chamber) => {

        const value =
            chamber?.occupancy_percent ??
            chamber?.occupancyPercentage ??
            chamber?.occupancy;

        if (
            value !== undefined &&
            value !== null
        ) {

            return Math.min(
                100,
                Math.max(
                    0,
                    Number(value)
                )
            );

        }

        const capacity =
            getCapacity(chamber);

        const occupied =
            getOccupied(chamber);

        if (capacity <= 0) {
            return 0;
        }

        return Math.min(
            100,
            Math.max(
                0,
                (occupied / capacity) * 100
            )
        );

    };


    // =====================================================
    // STATUS CLASS
    // =====================================================

    const getStatusClass = (status) => {

        const value =
            String(
                status || "ACTIVE"
            ).toUpperCase();

        if (value === "ACTIVE") {
            return "active";
        }

        if (value === "MAINTENANCE") {
            return "maintenance";
        }

        return "inactive";

    };


    // =====================================================
    // OCCUPANCY CLASS
    // =====================================================

    const getOccupancyClass = (percentage) => {

        if (percentage >= 90) {
            return "critical";
        }

        if (percentage >= 70) {
            return "high";
        }

        if (percentage >= 40) {
            return "medium";
        }

        return "low";

    };


    // =====================================================
    // OPEN CHAMBER INVENTORY
    // =====================================================

    const handleChamberClick = async (chamber) => {

        try {

            setSelectedChamber(chamber);

            setChamberInventory([]);

            setInventoryError("");

            setInventoryLoading(true);

            const response =
                await getInventoryByChamber(
                    chamber.id
                );

            const data =
                response?.data?.data ||
                response?.data ||
                [];

            setChamberInventory(
                Array.isArray(data)
                    ? data
                    : []
            );

        } catch (err) {

            console.error(
                "Chamber Inventory Error:",
                err
            );

            setInventoryError(
                err?.response?.data?.message ||
                "Unable to load goods stored in this chamber."
            );

        } finally {

            setInventoryLoading(false);

        }

    };


    // =====================================================
    // CLOSE INVENTORY
    // =====================================================

    const closeChamberInventory = () => {

        setSelectedChamber(null);

        setChamberInventory([]);

        setInventoryError("");

    };


    // =====================================================
    // LOADING
    // =====================================================

    if (loading) {

        return (

            <div className="chambers-page">

                <div className="chambers-loading">

                    <RefreshCw
                        size={30}
                        className="chambers-spin"
                    />

                    <h2>
                        Loading Chambers
                    </h2>

                    <p>
                        Fetching live chamber information...
                    </p>

                </div>

            </div>

        );

    }


    // =====================================================
    // PAGE
    // =====================================================

    return (

        <div className="chambers-page">


            {/* =================================================
                HEADER
            ================================================= */}

            <div className="chambers-header">

                <div className="chambers-title-section">

                    <div className="chambers-title-icon">

                        <Warehouse size={27} />

                    </div>

                    <div>

                        <h1>
                            Chambers
                        </h1>

                        <p>
                            Monitor cold storage capacity and live occupancy
                        </p>

                    </div>

                </div>


                <button
                    className="chambers-refresh-button"
                    onClick={handleRefresh}
                    disabled={refreshing}
                >

                    <RefreshCw
                        size={18}
                        className={
                            refreshing
                                ? "chambers-spin"
                                : ""
                        }
                    />

                    {refreshing
                        ? "Refreshing..."
                        : "Refresh"
                    }

                </button>

            </div>


            {/* =================================================
                ERROR
            ================================================= */}

            {error && (

                <div className="chambers-error">

                    <AlertTriangle size={20} />

                    <span>
                        {error}
                    </span>

                </div>

            )}


            {/* =================================================
                SUMMARY
            ================================================= */}

            <div className="chambers-summary">


                {/* TOTAL CHAMBERS */}

                <div className="chamber-summary-card">

                    <div className="summary-icon blue">

                        <Warehouse size={21} />

                    </div>

                    <div>

                        <span>
                            Total Chambers
                        </span>

                        <strong>
                            {chambers.length}
                        </strong>

                    </div>

                </div>


                {/* TOTAL CAPACITY */}

                <div className="chamber-summary-card">

                    <div className="summary-icon green">

                        <Package size={21} />

                    </div>

                    <div>

                        <span>
                            Total Capacity
                        </span>

                        <strong>

                            {formatNumber(
                                chambers.reduce(
                                    (
                                        total,
                                        chamber
                                    ) =>
                                        total +
                                        getCapacity(
                                            chamber
                                        ),
                                    0
                                )
                            )}

                            <small>
                                {" "}Tons
                            </small>

                        </strong>

                    </div>

                </div>


                {/* OCCUPIED */}

                <div className="chamber-summary-card">

                    <div className="summary-icon purple">

                        <Snowflake size={21} />

                    </div>

                    <div>

                        <span>
                            Occupied
                        </span>

                        <strong>

                            {formatNumber(
                                chambers.reduce(
                                    (
                                        total,
                                        chamber
                                    ) =>
                                        total +
                                        getOccupied(
                                            chamber
                                        ),
                                    0
                                )
                            )}

                            <small>
                                {" "}Tons
                            </small>

                        </strong>

                    </div>

                </div>


                {/* AVAILABLE */}

                <div className="chamber-summary-card">

                    <div className="summary-icon orange">

                        <CheckCircle2 size={21} />

                    </div>

                    <div>

                        <span>
                            Available
                        </span>

                        <strong>

                            {formatNumber(
                                chambers.reduce(
                                    (
                                        total,
                                        chamber
                                    ) =>
                                        total +
                                        getAvailable(
                                            chamber
                                        ),
                                    0
                                )
                            )}

                            <small>
                                {" "}Tons
                            </small>

                        </strong>

                    </div>

                </div>

            </div>


            {/* =================================================
                CHAMBERS
            ================================================= */}

            {chambers.length === 0 ? (

                <div className="chambers-empty">

                    <Warehouse size={45} />

                    <h2>
                        No Chambers Found
                    </h2>

                    <p>
                        No chamber records are available in the database.
                    </p>

                </div>

            ) : (

                <div className="chambers-grid">

                    {chambers.map(
                        (
                            chamber,
                            index
                        ) => {


                            // ---------------------------------
                            // DATABASE CHAMBER NUMBER
                            // ---------------------------------

                            const chamberNo =
                                chamber?.chamber_no ??
                                chamber?.chamberNo ??
                                `C-${String(
                                    index + 1
                                ).padStart(
                                    2,
                                    "0"
                                )}`;


                            // ---------------------------------
                            // DISPLAY NAME A/B/C/D
                            // ---------------------------------

                            const displayName =
                                getChamberDisplayName(
                                    chamberNo
                                );


                            // ---------------------------------
                            // OLD CHAMBER NAME
                            // ---------------------------------

                            const databaseChamberName =
                                chamber?.chamber_name ??
                                chamber?.chamberName ??
                                `Chamber ${index + 1}`;


                            // ---------------------------------
                            // VALUES
                            // ---------------------------------

                            const capacity =
                                getCapacity(chamber);

                            const occupied =
                                getOccupied(chamber);

                            const available =
                                getAvailable(chamber);

                            const occupancy =
                                getOccupancy(chamber);

                            const status =
                                chamber?.status ??
                                "ACTIVE";

                            const location =
                                chamber?.location ??
                                "-";

                            const temperature =
                                chamber?.temperature ??
                                "-";

                            const occupancyClass =
                                getOccupancyClass(
                                    occupancy
                                );


                            return (

                                <div
                                    className={
                                        `chamber-card ${occupancyClass}`
                                    }
                                    key={
                                        chamber?.id ??
                                        chamberNo
                                    }
                                    onClick={() =>
                                        handleChamberClick(
                                            chamber
                                        )
                                    }
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(event) => {

                                        if (
                                            event.key ===
                                            "Enter"
                                        ) {

                                            handleChamberClick(
                                                chamber
                                            );

                                        }

                                    }}
                                >


                                    {/* =================================
                                        CARD TOP
                                    ================================= */}

                                    <div className="chamber-card-top">

                                        <div className="chamber-card-icon">

                                            <Warehouse size={25} />

                                        </div>


                                        <span
                                            className={
                                                `chamber-status ${getStatusClass(
                                                    status
                                                )}`
                                            }
                                        >

                                            <span className="status-dot" />

                                            {String(
                                                status
                                            )}

                                        </span>

                                    </div>


                                    {/* =================================
                                        CHAMBER NAME
                                    ================================= */}

                                    <div className="chamber-name-section">

                                        {/* DISPLAY A/B/C/D */}

                                        <span className="chamber-number">

                                            {displayName}

                                        </span>


                                        <h2>

                                            Chamber {displayName}

                                        </h2>


                                        <span className="chamber-click-hint">

                                            Click to view stored goods

                                        </span>

                                    </div>


                                    {/* =================================
                                        STATS
                                    ================================= */}

                                    <div className="chamber-stats">


                                        {/* CAPACITY */}

                                        <div className="chamber-stat">

                                            <span>
                                                Capacity
                                            </span>

                                            <strong>

                                                {formatNumber(
                                                    capacity
                                                )}

                                                <small>
                                                    {" "}Tons
                                                </small>

                                            </strong>

                                        </div>


                                        {/* OCCUPIED */}

                                        <div className="chamber-stat">

                                            <span>
                                                Occupied
                                            </span>

                                            <strong>

                                                {formatNumber(
                                                    occupied
                                                )}

                                                <small>
                                                    {" "}Tons
                                                </small>

                                            </strong>

                                        </div>


                                        {/* AVAILABLE */}

                                        <div className="chamber-stat">

                                            <span>
                                                Available
                                            </span>

                                            <strong>

                                                {formatNumber(
                                                    available
                                                )}

                                                <small>
                                                    {" "}Tons
                                                </small>

                                            </strong>

                                        </div>

                                    </div>


                                    {/* =================================
                                        OCCUPANCY
                                    ================================= */}

                                    <div className="chamber-occupancy">

                                        <div className="occupancy-heading">

                                            <span>
                                                Occupancy
                                            </span>

                                            <strong>

                                                {occupancy.toFixed(2)}%

                                            </strong>

                                        </div>


                                        <div className="occupancy-bar">

                                            <div
                                                className="occupancy-fill"
                                                style={{
                                                    width:
                                                        `${occupancy}%`
                                                }}
                                            />

                                        </div>

                                    </div>


                                    {/* =================================
                                        INFO
                                    ================================= */}

                                    <div className="chamber-info">


                                        {/* LOCATION */}

                                        <div className="chamber-info-item">

                                            <div className="info-icon">

                                                <MapPin size={18} />

                                            </div>

                                            <div>

                                                <span>
                                                    Location
                                                </span>

                                                <strong>
                                                    {location}
                                                </strong>

                                            </div>

                                        </div>


                                        {/* TEMPERATURE */}

                                        <div className="chamber-info-item">

                                            <div className="info-icon">

                                                <Thermometer
                                                    size={18}
                                                />

                                            </div>

                                            <div>

                                                <span>
                                                    Temperature
                                                </span>

                                                <strong>
                                                    {temperature}
                                                </strong>

                                            </div>

                                        </div>

                                    </div>


                                    {/* =================================
                                        FOOTER
                                    ================================= */}

                                    <div className="chamber-card-footer">

                                        <span>
                                            Storage Chamber {displayName}
                                        </span>

                                        <span className="view-stock-text">

                                            <Eye size={16} />

                                            View Stock

                                        </span>

                                    </div>

                                </div>

                            );

                        }
                    )}

                </div>

            )}


            {/* =================================================
                CHAMBER INVENTORY MODAL
            ================================================= */}

            {selectedChamber && (

                <div
                    className="chamber-inventory-overlay"
                    onClick={closeChamberInventory}
                >

                    <div
                        className="chamber-inventory-modal"
                        onClick={(event) =>
                            event.stopPropagation()
                        }
                    >


                        {/* =================================
                            MODAL HEADER
                        ================================= */}

                        <div className="chamber-inventory-header">

                            <div>

                                <div className="inventory-modal-title">

                                    <div className="inventory-modal-icon">

                                        <Boxes size={23} />

                                    </div>

                                    <div>

                                        <h2>

                                            Chamber{" "}

                                            {
                                                getChamberDisplayName(
                                                    selectedChamber?.chamber_no ??
                                                    selectedChamber?.chamberNo
                                                )
                                            }

                                        </h2>

                                        <p>
                                            All goods currently stored in this chamber
                                        </p>

                                    </div>

                                </div>

                            </div>


                            <button
                                className="chamber-inventory-close"
                                onClick={
                                    closeChamberInventory
                                }
                                title="Close"
                            >

                                <X size={21} />

                            </button>

                        </div>


                        {/* =================================
                            INVENTORY BODY
                        ================================= */}

                        <div className="chamber-inventory-body">


                            {/* LOADING */}

                            {inventoryLoading ? (

                                <div className="inventory-loading">

                                    <RefreshCw
                                        size={28}
                                        className="chambers-spin"
                                    />

                                    <p>
                                        Loading goods stored in this chamber...
                                    </p>

                                </div>


                            ) : inventoryError ? (


                                /* ERROR */

                                <div className="inventory-modal-error">

                                    <AlertTriangle
                                        size={21}
                                    />

                                    {inventoryError}

                                </div>


                            ) : chamberInventory.length === 0 ? (


                                /* EMPTY */

                                <div className="inventory-empty">

                                    <Package size={42} />

                                    <h3>
                                        No Goods Stored
                                    </h3>

                                    <p>
                                        There are currently no active lots in this chamber.
                                    </p>

                                </div>


                            ) : (

                                <>


                                    {/* =================================
                                        STOCK SUMMARY
                                    ================================= */}

                                    <div className="inventory-stock-summary">


                                        {/* ACTIVE LOTS */}

                                        <div>

                                            <span>
                                                Active Lots
                                            </span>

                                            <strong>
                                                {chamberInventory.length}
                                            </strong>

                                        </div>


                                        {/* REMAINING BAGS */}

                                        <div>

                                            <span>
                                                Remaining Bags
                                            </span>

                                            <strong>

                                                {formatNumber(
                                                    chamberInventory.reduce(
                                                        (
                                                            total,
                                                            item
                                                        ) =>
                                                            total +
                                                            Number(
                                                                item?.remaining_bags ??
                                                                item?.remainingBags ??
                                                                0
                                                            ),
                                                        0
                                                    )
                                                )}

                                            </strong>

                                        </div>


                                        {/* REMAINING WEIGHT */}

                                        <div>

                                            <span>
                                                Remaining Weight
                                            </span>

                                            <strong>

                                                {formatNumber(
                                                    chamberInventory.reduce(
                                                        (
                                                            total,
                                                            item
                                                        ) =>
                                                            total +
                                                            Number(
                                                                item?.remaining_weight_kg ??
                                                                item?.remainingWeightKg ??
                                                                0
                                                            ),
                                                        0
                                                    ) / 1000
                                                )}

                                                {" "}Tons

                                            </strong>

                                        </div>

                                    </div>


                                    {/* =================================
                                        TABLE
                                    ================================= */}

                                    <div className="chamber-inventory-table-wrapper">

                                        <table className="chamber-inventory-table">

                                            <thead>

                                                <tr>

                                                    <th>
                                                        Lot No.
                                                    </th>

                                                    <th>
                                                        Customer
                                                    </th>

                                                    <th>
                                                        Product Mark
                                                    </th>

                                                    <th>
                                                        Received Bags
                                                    </th>

                                                    <th>
                                                        Remaining Bags
                                                    </th>

                                                    <th>
                                                        Remaining Weight
                                                    </th>

                                                    <th>
                                                        Location
                                                    </th>

                                                    <th>
                                                        Status
                                                    </th>

                                                </tr>

                                            </thead>


                                            <tbody>

                                                {chamberInventory.map(
                                                    (
                                                        item,
                                                        index
                                                    ) => (

                                                        <tr
                                                            key={
                                                                item?.id ??
                                                                index
                                                            }
                                                        >


                                                            {/* LOT */}

                                                            <td>

                                                                <strong>

                                                                    {
                                                                        item?.lot_no ??
                                                                        item?.lotNo ??
                                                                        "-"
                                                                    }

                                                                </strong>

                                                            </td>


                                                            {/* CUSTOMER */}

                                                            <td>

                                                                {
                                                                    item?.customer_name ??
                                                                    item?.customerName ??
                                                                    "-"
                                                                }

                                                            </td>


                                                            {/* PRODUCT MARK */}

                                                            <td>

                                                                {
                                                                    item?.product_mark ??
                                                                    item?.productMark ??
                                                                    "-"
                                                                }

                                                            </td>


                                                            {/* RECEIVED */}

                                                            <td>

                                                                {formatNumber(
                                                                    item?.received_bags ??
                                                                    item?.receivedBags ??
                                                                    0
                                                                )}

                                                            </td>


                                                            {/* REMAINING BAGS */}

                                                            <td>

                                                                <strong className="remaining-bags">

                                                                    {formatNumber(
                                                                        item?.remaining_bags ??
                                                                        item?.remainingBags ??
                                                                        0
                                                                    )}

                                                                </strong>

                                                            </td>


                                                            {/* REMAINING WEIGHT */}

                                                            <td>

                                                                {formatNumber(
                                                                    Number(
                                                                        item?.remaining_weight_kg ??
                                                                        item?.remainingWeightKg ??
                                                                        0
                                                                    ) / 1000
                                                                )}

                                                                {" "}Tons

                                                            </td>


                                                            {/* LOCATION */}

                                                            <td>

                                                                {
                                                                    item?.location ??
                                                                    "-"
                                                                }

                                                            </td>


                                                            {/* STATUS */}

                                                            <td>

                                                                <span className="inventory-status">

                                                                    {
                                                                        item?.status ??
                                                                        "ACTIVE"
                                                                    }

                                                                </span>

                                                            </td>

                                                        </tr>

                                                    )
                                                )}

                                            </tbody>

                                        </table>

                                    </div>

                                </>

                            )}

                        </div>

                    </div>

                </div>

            )}

        </div>

    );

};


export default Chambers;